import { c } from '#classnames.ts';
import { fetchFormData } from '#fetch_formdata.ts';
import type { MediaFolder } from '#folders.ts';
import { AttachmentIcon, Loading01Icon, Upload02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useState } from 'react';

const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

enum Status {
	None,
	Loading,
	Done,
}

export interface FolderPageProps {
	folder: MediaFolder;
}

export const FolderPage: React.FC<FolderPageProps> = ({ folder }) => {
	const [files, setFiles] = useState<File[]>([]);
	const [status, setStatus] = useState(Status.None);
	const [uploadPercentage, setUploadPercentage] = useState(0);

	return (
		<main className='relative mx-auto flex h-full w-full max-w-120 flex-col gap-3 px-3 pt-18 pb-3 text-orange-950'>
			<p
				className='text-center text-3xl font-bold text-balance whitespace-pre-wrap uppercase sm:text-4xl md:text-5xl'
				style={{ color: folder.color }}>
				{folder.displayName}
			</p>
			<p className='text-center text-lg font-bold text-balance'>
				Grazie per esserci stat* <span className='align-text-top leading-none'>🧡</span>
				<br />
				Carica qui le tue foto e i tuoi video
			</p>

			<div>
				<div
					className={c(
						'relative flex h-12 items-center justify-center gap-2 border-2 border-orange-400 bg-white p-3 text-orange-400 uppercase',
						status === Status.Loading
							? 'text-orange-300'
							: 'text-orange-50 hover:bg-orange-50 active:bg-orange-100',
					)}>
					<HugeiconsIcon icon={AttachmentIcon} strokeWidth={2} className='size-5' />
					<span className='tracking-wider'>
						{files.length === 0 ? 'Seleziona i' : 'Aggiungi'} File
					</span>
					<input
						className='absolute -inset-0.5 cursor-pointer overflow-clip opacity-0'
						accept={IS_IOS ? undefined : 'image/*,video/*,.heic,.heif'}
						multiple
						type='file'
						onChange={(e) => {
							const newFiles = e.target.files;
							setStatus(Status.None);
							setFiles((oldFiles) =>
								[...oldFiles, ...(newFiles ?? [])].filter(
									(f, i, ff) => ff.findIndex((x) => x.name + x.size === f.name + f.size) === i,
								),
							);
						}}
					/>
				</div>
			</div>

			{files.length === 0 && status === Status.Done && (
				<div className='my-4 text-center text-sm'>🧡🧡🧡 Grazie 🧡🧡🧡</div>
			)}

			{status === Status.Loading && (
				<div className='fixed inset-0 z-50 flex flex-col items-center justify-center bg-orange-50/80 p-6 text-center text-orange-500 backdrop-blur-xl'>
					<p className='text-3xl'>{uploadPercentage}%</p>
					<p className='text-xl'>Stiamo caricando i file...</p>
					<p className='text-lg'>(non chiudere la pagina!)</p>
				</div>
			)}

			{files.length > 0 && (
				<>
					<p className='text-center text-sm italic'>
						Hai selezionato{' '}
						{
							files.filter(
								(f) =>
									f.type.startsWith('image/') ||
									f.name.toLowerCase().endsWith('.heic') ||
									f.name.toLowerCase().endsWith('.heif'),
							).length
						}{' '}
						foto e {files.filter((f) => f.type.startsWith('video/')).length} video
					</p>
					<div
						className='sticky bottom-3 z-20 flex h-12 cursor-pointer items-center justify-center gap-2 bg-orange-400 p-3 text-white shadow-2xl shadow-orange-400/50 hover:brightness-105 active:brightness-110'
						tabIndex={0}
						onClick={async () => {
							setStatus(Status.Loading);
							const data = new FormData();
							data.append('count', files.length.toString());
							data.append('folder_name', folder.folderName);
							for (let i = 0; i < files.length; i++) {
								data.append(`file-${i}`, files[i]!);
							}
							await fetchFormData('post', '/api/upload', data, (e) => {
								setUploadPercentage(Math.floor((e.loaded / e.total) * 100));
							});
							setStatus(Status.Done);
							setFiles([]);
						}}>
						<HugeiconsIcon
							icon={status === Status.Loading ? Loading01Icon : Upload02Icon}
							strokeWidth={2}
							className='size-5'
						/>
						<span className='text-sm tracking-widest'>
							{status === Status.Loading ? 'Caricando' : 'Carica'}
						</span>
					</div>
				</>
			)}
		</main>
	);
};
